import { ClockCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Descriptions, FloatButton, Input, Modal, Select, Space, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMyPendingApprovalCount, getMyPendingApprovals, resendPendingApproval } from '@/services/pendingApprovals';
import { getErrorMessageFromResponse } from '@/utils/utils';

const REFRESH_INTERVAL = 60 * 1000;
const APPLICATION_TITLE = '我的赠课/赠积分、暂停合同、合同置换待审批申请';
const statusLabels = { PENDING: '待处理', APPROVED: '已通过', REJECTED: '已拒绝' };
const statusColors = { PENDING: 'processing', APPROVED: 'success', REJECTED: 'error' };
const deliveryLabels = { sent: '已发送', failed: '发送失败', unknown: '发送状态未确认' };
const formatTime = (value) => value && dayjs(value).isValid()
  ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—';

function getResendDisabledReason(approval, sendingId) {
  if (typeof approval.canResend !== 'boolean') {
    return '未获取到重发状态，请刷新页面；若仍未恢复，请联系管理员检查服务版本。';
  }
  if (!approval.canResend) return approval.resendDisabledReason || '当前无法重发，请刷新申请状态。';
  if (sendingId !== null && sendingId !== approval.id) return '正在发送其他审批消息，请稍候。';
  return '';
}

function ApprovalProgress({ record, onResend, sendingId }) {
  if (!record.approvals.length) {
    const text = record.cardApprovals
      ? (record.legacyApproval ? '旧版飞书审批，暂无逐人审批进度。' : '未查到审批人记录，请联系管理员核对发送情况。')
      : record.progressHint;
    return <Typography.Text type="secondary">{text}</Typography.Text>;
  }
  return (
    <Space direction="vertical">
      {record.approvals.map((approval) => (
        <Space key={approval.id} wrap>
          <span>{approval.approverName}</span>
          <Tag color={statusColors[approval.status]}>{statusLabels[approval.status]}</Tag>
          <Typography.Text type={approval.deliveryStatus === 'failed' ? 'danger' : 'secondary'}>
            {deliveryLabels[approval.deliveryStatus]}
          </Typography.Text>
          <Typography.Text type="secondary">消息发送时间：{approval.sentAt ? formatTime(approval.sentAt) : '未记录'}</Typography.Text>
          {approval.approvedAt && <span>处理时间：{formatTime(approval.approvedAt)}</span>}
          {approval.lastResentAt && <Typography.Text type="secondary">最近重发：{formatTime(approval.lastResentAt)}</Typography.Text>}
          {record.cardApprovals && approval.status === 'PENDING' && (
            <>
              <Button
                size="small"
                loading={sendingId === approval.id}
                disabled={!!getResendDisabledReason(approval, sendingId)}
                onClick={() => onResend(record, approval)}
              >重新发送</Button>
              {getResendDisabledReason(approval, sendingId) && (
                <Typography.Text type="secondary">{getResendDisabledReason(approval, sendingId)}</Typography.Text>
              )}
              {approval.resendStatus === 'failed' && <Typography.Text type="danger">上次重发失败</Typography.Text>}
              {approval.resendStatus === 'unknown' && <Typography.Text type="warning">上次重发结果未确认，请先查看飞书</Typography.Text>}
            </>
          )}
        </Space>
      ))}
    </Space>
  );
}

function ApplicationDetails({ record, onResend, sendingId }) {
  const items = [
    { label: '申请编号', value: `${record.title} #${record.id}` },
    { label: record.timeLabel, value: formatTime(record.createdAt) },
    { label: '学生', value: record.studentName },
    { label: '相关人员', value: record.personName },
    { label: '合同号', value: record.contractNumber },
    { label: '产品', value: record.productName },
    ...record.details,
  ].filter((item) => item.value);
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%', padding: 8 }}>
      <Descriptions column={{ xs: 1, sm: 2 }} size="small" bordered>
        {items.map((item) => (
          <Descriptions.Item key={item.label} label={item.label}>
            <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.value}</span>
          </Descriptions.Item>
        ))}
      </Descriptions>
      <div><Typography.Text strong>审批进度</Typography.Text></div>
      {record.approvalMode === 'ALL' && (
        <Typography.Text type="secondary">审批规则：需全部审批人通过，其余待处理审批人仍需处理。</Typography.Text>
      )}
      <ApprovalProgress record={record} onResend={onResend} sendingId={sendingId} />
      {record.cardApprovals && record.approvals.some((approval) => approval.status === 'PENDING') && (
        <Typography.Text type="secondary">重新发送会向该审批人发送新的飞书消息，每位审批人每 5 分钟可发送一次。</Typography.Text>
      )}
      {record.ownershipNote && <Typography.Text type="secondary">{record.ownershipNote}</Typography.Text>}
    </Space>
  );
}

export default function MyPendingApprovals({ userId }) {
  const [open, setOpen] = useState(false);
  const [total, setTotal] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [type, setType] = useState();
  const [page, setPage] = useState(1);
  const [sendingId, setSendingId] = useState(null);
  const [resendNotice, setResendNotice] = useState(null);
  const sending = useRef(false);
  const mounted = useRef(false);
  const openRef = useRef(false);
  const requestId = useRef(0);
  const inFlight = useRef(false);

  const refresh = useCallback(async (withDetails = false) => {
    if (!userId || !mounted.current) return;
    const currentRequest = ++requestId.current;
    inFlight.current = true;
    setLoading(true);
    try {
      const result = await (withDetails ? getMyPendingApprovals() : getMyPendingApprovalCount());
      if (!Number.isSafeInteger(result?.total) || result.total < 0
        || (withDetails && !Array.isArray(result.data))) {
        throw new Error('Invalid pending approval response');
      }
      if (!mounted.current || currentRequest !== requestId.current) return;
      setTotal(result.total);
      if (withDetails) setRows(result.data);
      setError(false);
    } catch {
      if (mounted.current && currentRequest === requestId.current) setError(true);
    } finally {
      if (mounted.current && currentRequest === requestId.current) {
        inFlight.current = false;
        setLoading(false);
      }
    }
  }, [userId]);

  const resend = async (record, approval) => {
    if (sending.current || approval.canResend !== true) return;
    sending.current = true;
    setSendingId(approval.id);
    setResendNotice(null);
    try {
      const result = await resendPendingApproval(record.type, record.id, approval.id);
      if (!mounted.current) return;
      if (result?.result !== 'sent') throw new Error('未能确认发送结果，请刷新后查看');
      setRows((current) => current.map((row) => row.key === record.key ? {
        ...row,
        approvals: row.approvals.map((item) => item.id === approval.id ? { ...item, ...result } : item),
      } : row));
      setResendNotice({ type: 'success', message: `已重新向${approval.approverName}发送飞书审批消息` });
    } catch (failure) {
      if (mounted.current) setResendNotice({ type: 'error', message: getErrorMessageFromResponse(failure) });
    } finally {
      sending.current = false;
      if (mounted.current) {
        setSendingId(null);
        refresh(true);
      }
    }
  };

  useEffect(() => {
    if (!userId) return undefined;
    mounted.current = true;
    setTotal(null);
    setRows([]);
    setError(false);
    setOpen(false);
    openRef.current = false;
    refresh();
    const refreshVisible = () => {
      if (document.visibilityState !== 'hidden' && !inFlight.current) refresh(openRef.current);
    };
    const timer = window.setInterval(refreshVisible, REFRESH_INTERVAL);
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      mounted.current = false;
      requestId.current += 1;
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [userId, refresh]);

  const types = useMemo(() => [...new Map(rows.map((row) => [row.type, row.title]))]
    .map(([value, label]) => ({ value, label })), [rows]);
  const filteredRows = useMemo(() => {
    const search = keyword.trim().toLowerCase();
    return rows.filter((row) => (!type || row.type === type)
      && (!search || [row.id, row.title, row.studentName, row.personName, row.contractNumber,
        ...row.details.map((detail) => detail.value)].join(' ').toLowerCase().includes(search)));
  }, [rows, keyword, type]);
  const currentPage = Math.min(page, Math.max(1, Math.ceil(filteredRows.length / 10)));

  if (!userId) return null;

  const columns = [
    { title: '申请类型', dataIndex: 'title', width: 135 },
    { title: '编号', dataIndex: 'id', width: 80 },
    {
      title: '学生 / 相关人员', key: 'subject', width: 160,
      render: (_, row) => row.studentName || row.personName || '—',
    },
    { title: '合同号', dataIndex: 'contractNumber', width: 170, render: (value) => value || '—' },
    { title: '记录时间', dataIndex: 'createdAt', width: 155, render: formatTime },
    {
      title: '进度', key: 'progress', width: 160,
      render: (_, row) => row.approvals.length
        ? `${row.approvals.filter((approval) => approval.status !== 'PENDING').length}/${row.approvals.length} 人已处理`
        : <Tag color="processing">待审批</Tag>,
    },
  ];

  return (
    <>
      <FloatButton
        icon={<ClockCircleOutlined />}
        aria-label={APPLICATION_TITLE}
        tooltip={error ? `${APPLICATION_TITLE}更新失败，点击重试` : `${APPLICATION_TITLE}：${total ?? '加载中'}`}
        badge={{ count: error ? '!' : (total ?? '…'), showZero: true, overflowCount: 999 }}
        onClick={() => {
          openRef.current = true;
          setOpen(true);
          refresh(true);
        }}
      />
      <Modal
        title={`${APPLICATION_TITLE}${total === null ? '' : `（${total}）`}`}
        open={open}
        width={1100}
        footer={null}
        onCancel={() => { openRef.current = false; setOpen(false); }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text type="secondary">展示本人发起且尚未完成审批的申请，展开每条记录查看详情。</Typography.Text>
          {error && <Alert type="warning" showIcon message="暂时无法更新申请记录，请重试。已有记录可能不是最新状态。" />}
          {resendNotice && <Alert type={resendNotice.type} showIcon message={resendNotice.message} closable onClose={() => setResendNotice(null)} />}
          <Space wrap>
            <Select
              aria-label="申请类型"
              placeholder="全部申请类型"
              allowClear
              value={type}
              options={types}
              style={{ width: 180 }}
              onChange={(value) => { setType(value); setPage(1); }}
            />
            <Input
              aria-label="搜索申请"
              placeholder="搜索编号、学生、人员、合同或申请内容"
              allowClear
              value={keyword}
              style={{ width: 320, maxWidth: '100%' }}
              onChange={(event) => { setKeyword(event.target.value); setPage(1); }}
            />
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => refresh(true)}>刷新</Button>
          </Space>
          <Table
            rowKey="key"
            size="small"
            columns={columns}
            dataSource={filteredRows}
            loading={loading}
            scroll={{ x: 960 }}
            pagination={{ current: currentPage, pageSize: 10, showSizeChanger: false, onChange: setPage }}
            locale={{ emptyText: error ? '申请记录加载失败，请点击刷新' : '暂无符合条件的待审批申请' }}
            expandable={{
              expandedRowRender: (record) => <ApplicationDetails record={record} onResend={resend} sendingId={sendingId} />,
              columnTitle: '详情',
            }}
          />
        </Space>
      </Modal>
    </>
  );
}
